import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import { generateEventSchema } from './schemaBuilder';

import {
  dappSessionSelectorSchema,
  logLevelSchema,
  popupFeaturesSchema,
  systemConfigSchema,
} from '../connect';
import { storedDataSchema } from '../storage';

export const HandshakeMethods = [
  'channel-pair-request',
  'channel-ready',

  'popup-pair-request',
  'popup-ready',
] as const;

const namespace: InfinexMessageNamespace = 'handshake';

const windowDimensionsSchema = z
  .object({
    screen: z
      .object({
        availHeight: z.number(),
        availWidth: z.number(),
        height: z.number(),
        width: z.number(),
      })
      .catchall(z.any()),
    innerWidth: z.number(),
    innerHeight: z.number(),
    outerWidth: z.number(),
    outerHeight: z.number(),
    screenX: z.number(),
    screenY: z.number(),
  })
  .catchall(z.any());

export type WindowDimensions = z.infer<typeof windowDimensionsSchema>;

// Response shape for “channel-pair-request”
const channelPairResponseSchema = z
  .object({
    appKey: z.string(),
    storedData: storedDataSchema,
    logLevel: logLevelSchema,
    windowDimensions: windowDimensionsSchema,
  })
  .strict();

// “popup-pair-request” has a request shape:
export const channelPairRequestSchema = generateEventSchema({
  namespace,
  method: 'channel-pair-request',

  requestData: systemConfigSchema,
  responseData: channelPairResponseSchema,
  notificationData: z.never(),
});

export const channelReadySchema = generateEventSchema({
  namespace,
  method: 'channel-ready',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      storedData: storedDataSchema,
      popupFeatures: popupFeaturesSchema,
    })
    .catchall(z.any()),
});

export const popupPairRequestSchema = generateEventSchema({
  namespace,
  method: 'popup-pair-request',

  requestData: z
    .object({
      dappSessionIdentifier: dappSessionSelectorSchema
        .catchall(z.any())
        .nullable(),
    })
    .catchall(z.any()),
  responseData: z
    .object({
      popupId: z.string(),
    })
    .catchall(z.any()),

  notificationData: z.never(),
});

export const popupReadySchema = generateEventSchema({
  namespace,
  method: 'popup-ready',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z.any(),
});

/* ─────────────────── Aggregated schema arrays ─────────────────── */
export const handshakeRequestParamSchemas = [
  channelPairRequestSchema.requestParamsSchema,
  popupPairRequestSchema.requestParamsSchema,
] as const;

export const handshakeResponseParamSchemas = [
  channelPairRequestSchema.responseParamsSchema,
  popupPairRequestSchema.responseParamsSchema,
] as const;

export const handshakeNotificationParamSchemas = [
  channelReadySchema.notificationParamsSchema,
  popupReadySchema.notificationParamsSchema,
] as const;

export const handshakeRequestEventSchemas = [
  channelPairRequestSchema.requestSchema,
  popupPairRequestSchema.requestSchema,
] as const;

export const handshakeResponseEventSchemas = [
  channelPairRequestSchema.responseSchema,
  popupPairRequestSchema.responseSchema,
] as const;

export const handshakeNotificationEventSchemas = [
  channelReadySchema.notificationSchema,
  popupReadySchema.notificationSchema,
] as const;
